import React, {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext';
import { MIS_COLLECTION_REPORT, MIS_COLLECTION_REPORT_TITLE } from '../Constants/strings';
import ExcelJS from 'exceljs';
import { useSnackbar } from '../Components/Snackbar/snackBarProvider';
import exportToExcel, { createExcelRows, toTitleCase } from '../Utils/exportToExcel';

export const CollectionReportContext = createContext();

export const CollectionReportProvider = ({ children }) => {
  const { userProfile } = useAuth();
  const { snackbarOpen } = useSnackbar();
  const [downloadAllLoading, setDownloadAllLoading] = useState(false);
  const [tableState, setTableState] = useState(() => {
    return Object.values(MIS_COLLECTION_REPORT).reduce((acc, table) => {
      acc[table] = false;
      return acc;
    }, {});
  });
  const downloadHandlers = useRef(
    Object.values(MIS_COLLECTION_REPORT).reduce((acc, table) => {
      acc[table] = {
        handler: null,
        downloadDataHandler: null,
        name: table,
      };
      return acc;
    }, {}),
  );

  const setDownloadDataHandlers = useCallback((table, downloadHandler) => {
    if (downloadHandlers.current[table]) {
      downloadHandlers.current[table] = {
        ...downloadHandlers.current[table],
        downloadDataHandler: downloadHandler,
        handler: async () => {
          try {
            const data = await downloadHandler();
            return exportToExcel({
              data,
              workBookTitle: downloadHandlers.current[table].name,
              workSheetTitle: downloadHandlers.current[table].name,
            });
          } catch (error) {
            snackbarOpen(
              `${downloadHandlers.current[table].name} export failed`,
              'error',
            );
          }
        },
      };
    }
  }, []);

  const onDownloadAllClick = async () => {
    try {
      setDownloadAllLoading(true);
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Darpan 2';
      workbook.created = new Date();
      workbook.modified = new Date();
      workbook.lastPrinted = new Date();
      workbook.title = MIS_COLLECTION_REPORT_TITLE;

      await Promise.all(
        Object.values(downloadHandlers.current).map(async (obj) => {
          if (!obj.downloadDataHandler) {
            return;
          }
          const data = await obj.downloadDataHandler();
          const worksheet = workbook.addWorksheet(obj.name);
          if (data.length > 0) {
            const columns = Object.keys(data[0])
              .filter((key) => key !== 'format')
              .map((key) => ({
                header: toTitleCase(key),
                key: key,
                width: 30,
              }));
            worksheet.columns = columns;
            const headerRow = worksheet.getRow(1);
            headerRow.font = { bold: true };
            headerRow.eachCell((cell, colNumber) => {
              cell.alignment = {
                horizontal: colNumber === 1 ? 'left' : 'right',
              };
            });
            createExcelRows({ data, worksheet, columns });
          }
        }),
      );

      const newOrder = Object.values(MIS_COLLECTION_REPORT).map(name =>
        name.substring(0, 31).trim()
          .toLowerCase(),
      );
      const sortedWorksheets = workbook.worksheets.sort((first, second) => {
        let indexA = newOrder.indexOf(first.name.trim().toLowerCase());
        let indexB = newOrder.indexOf(second.name.trim().toLowerCase());
        if (indexA === -1) indexA = Infinity;
        if (indexB === -1) indexB = Infinity;
        return indexA - indexB;
      });

      sortedWorksheets.forEach((ws, index) => {
        ws.orderNo = index + 1;
      });

      if (workbook.model?.sheets) {
        workbook.model.sheets = sortedWorksheets.map((ws) => ws.model);
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${MIS_COLLECTION_REPORT_TITLE}.xlsx`;
      link.click();

      setDownloadAllLoading(false);
    } catch (error) {
      setDownloadAllLoading(false);
      snackbarOpen(
        'Reports download failed',
        'error',
      );
    }
  };

  const [expandAll, setExpandAll] = useState(false);
  const expandAllHandler = () => {
    const newExpandState = !expandAll;
    setExpandAll(newExpandState);
    setTableState((prevState) => {
      const newState = {};
      for (const key in prevState) {
        newState[key] = newExpandState;
      }
      return newState;
    });
  };

  const expandIndividual = (tableName) => {
    if (!Object.values(MIS_COLLECTION_REPORT).includes(tableName)) return;
    setTableState((prev) => {
      const newState = {
        ...prev,
        [tableName]: !prev[tableName],
      };
      const checkAllExpandedState = Object.values(newState).every((state) => state);
      setExpandAll(checkAllExpandedState);
      return newState;
    });
  };

  const value = useMemo(
    () => ({
      empDetails: {
        emailId: userProfile.email,
        fullName: userProfile.user_name,
        employeeId: userProfile.employeeId,
        permissions: userProfile.permissions,
        access_token: userProfile.access_token,
      },
      allExpanded: expandAll,
      expandAllHandler,
      expandIndividual,
      downloadHandlers,
      createDownloadAllHandler: setDownloadDataHandlers,
      accordionState: tableState,
      downloadAllLoading,
      onDownloadAllClick,
    }),
    [userProfile, expandAll, tableState, downloadHandlers, downloadAllLoading],
  );
  return (
    <CollectionReportContext.Provider value={value}>
      {children}
    </CollectionReportContext.Provider>
  );
};

CollectionReportProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
